// Morrow — Connect your own MCP server.
//
// For tools Morrow doesn't ship: point it at any remote MCP server (Streamable
// HTTP) and the agent can use that server's tools alongside the user's other
// apps. Opened as its own page from the Connections tab, like Business Email.
// The server CHECKS the URL answers tools/list before storing anything, and
// keeps the token/header encrypted — it's never sent back to the browser.
//
// Backed by /api/morrow/mcp/servers (+ /refresh, /remove). Requests carry the
// Clerk token; the server acts only for the verified signed-in user.

import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  Loader2Icon,
  RefreshCwIcon,
  ServerIcon,
  ShieldCheckIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { getClerkToken } from "../../clerkTokenBridge";
import { cn } from "../../lib/utils";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SettingsPageContainer } from "./settingsLayout";

type AuthType = "none" | "bearer" | "header" | "oauth";
export type KnownMcpServer = { name: string; url: string; authType: string; tagline: string };
export type McpServer = {
  id: string;
  name: string;
  slug: string;
  url: string;
  authType: string;
  status: string;
  toolCount: number;
  lastError: string | null;
  createdAt: string;
};

async function mcpRequest<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<{ ok: boolean; data: (T & { error?: string }) | null }> {
  try {
    const token = await getClerkToken().catch(() => null);
    const headers: Record<string, string> = { accept: "application/json" };
    if (token) headers["x-clerk-token"] = token;
    if (body) headers["content-type"] = "application/json";
    const res = await fetch(apiUrl(path), {
      method: body ? "POST" : "GET",
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
    return { ok: res.ok, data };
  } catch {
    return { ok: false, data: null };
  }
}

/** The signed-in user's attached MCP servers ([] when signed out or on error). */
export async function fetchMcpServers(): Promise<McpServer[]> {
  const { ok, data } = await mcpRequest<{ servers?: McpServer[] }>("/api/morrow/mcp/servers");
  return ok && Array.isArray(data?.servers) ? data.servers : [];
}

/** Curated one-click connectors (Plaud …). */
export async function fetchKnownMcpServers(): Promise<KnownMcpServer[]> {
  const { ok, data } = await mcpRequest<{ known?: KnownMcpServer[] }>("/api/morrow/mcp/known");
  return ok && Array.isArray(data?.known) ? data.known : [];
}

/**
 * Run the OAuth popup: open it synchronously (so the blocker allows it), ask the server for the
 * authorize URL, point the popup at it, and resolve when the callback posts back or the popup
 * closes. Returns the error string, or null on success.
 */
export async function connectOAuthPopup(url: string, name: string): Promise<string | null> {
  const popup = window.open("about:blank", "morrow-mcp-oauth", "width=520,height=720");
  if (!popup) return "Allow pop-ups for this site, then try again.";
  const { ok, data } = await mcpRequest<{ authorizeUrl?: string }>("/api/morrow/mcp/oauth/start", {
    url,
    name,
  });
  if (!ok || !data?.authorizeUrl) {
    popup.close();
    return data?.error ?? "Couldn't start the sign-in.";
  }
  popup.location.href = data.authorizeUrl;
  return await new Promise<string | null>((resolve) => {
    let done = false;
    const finish = (err: string | null) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      clearInterval(poll);
      resolve(err);
    };
    const onMessage = (e: MessageEvent) => {
      // Only trust a message from the popup WE opened. The callback posts with targetOrigin "*"
      // (it can't know the app origin) and carries no secret, so the source-window check — not an
      // origin string — is what stops another page from spoofing a "connected" result.
      if (e.source !== popup) return;
      const d = e.data as { type?: string; ok?: boolean } | null;
      if (d && d.type === "morrow-mcp-oauth") {
        finish(d.ok ? null : "Sign-in didn't complete.");
      }
    };
    window.addEventListener("message", onMessage);
    // Fallback: if the popup is closed without a message, stop waiting after it's gone.
    const poll = setInterval(() => {
      if (popup.closed) finish(null);
    }, 800);
    // Hard timeout so a stuck flow doesn't hang the button forever.
    setTimeout(() => finish(null), 5 * 60_000);
  });
}

const AUTH_OPTIONS: ReadonlyArray<{ id: AuthType; label: string }> = [
  { id: "none", label: "No auth" },
  { id: "bearer", label: "Bearer token" },
  { id: "header", label: "Custom header" },
  { id: "oauth", label: "OAuth (sign in)" },
];

const LabeledInput = ({
  label,
  ...props
}: { label: string } & React.ComponentProps<typeof Input>) => (
  <label className="flex flex-1 flex-col gap-1">
    <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
    <Input {...props} className={cn("h-9", props.className)} />
  </label>
);

export function McpConnectPage() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();

  const [servers, setServers] = useState<McpServer[]>([]);
  const [known, setKnown] = useState<KnownMcpServer[]>([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState<AuthType>("none");
  const [token, setToken] = useState("");
  const [headerName, setHeaderName] = useState("X-API-Key");
  const [headerValue, setHeaderValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickBusy, setQuickBusy] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshList = useCallback(() => {
    if (!isSignedIn) return;
    void fetchMcpServers().then(setServers);
  }, [isSignedIn]);
  useEffect(() => refreshList(), [refreshList]);
  useEffect(() => {
    void fetchKnownMcpServers().then(setKnown);
  }, []);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    const serverName = name.trim() || hostLabel(url);
    if (authType === "oauth") {
      const err = await connectOAuthPopup(url.trim(), serverName);
      setBusy(false);
      if (err) {
        setError(err);
        return;
      }
      setName("");
      setUrl("");
      refreshList();
      return;
    }
    const { ok, data } = await mcpRequest<{ server?: McpServer }>("/api/morrow/mcp/servers", {
      name: serverName,
      url: url.trim(),
      authType,
      ...(authType === "bearer" ? { token } : {}),
      ...(authType === "header" ? { headerName, headerValue } : {}),
    });
    setBusy(false);
    if (!ok || !data?.server) {
      setError(data?.error ?? "Couldn't connect to that MCP server.");
      return;
    }
    setName("");
    setUrl("");
    setToken("");
    setHeaderValue("");
    refreshList();
  }, [authType, headerName, headerValue, name, refreshList, token, url]);

  const quickConnect = useCallback(
    async (k: KnownMcpServer) => {
      setQuickBusy(k.url);
      setError(null);
      const err = await connectOAuthPopup(k.url, k.name);
      setQuickBusy(null);
      if (err) setError(err);
      else refreshList();
    },
    [refreshList],
  );

  const recheck = useCallback(
    async (id: string) => {
      setRowBusy(id);
      await mcpRequest("/api/morrow/mcp/servers/refresh", { id });
      setRowBusy(null);
      refreshList();
    },
    [refreshList],
  );

  const remove = useCallback(
    async (id: string) => {
      setRowBusy(id);
      await mcpRequest("/api/morrow/mcp/servers/remove", { id });
      setRowBusy(null);
      refreshList();
    },
    [refreshList],
  );

  const canConnect =
    url.trim().startsWith("https://") &&
    !busy &&
    (authType === "none" ||
      authType === "oauth" ||
      (authType === "bearer" && token.trim().length > 0) ||
      (authType === "header" && headerName.trim().length > 0 && headerValue.trim().length > 0));

  const suggested = known.filter((k) => !servers.some((s) => s.url === k.url));

  return (
    <SettingsPageContainer>
      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6">
        <button
          type="button"
          onClick={() => void navigate({ to: "/settings/connections" })}
          className="flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" /> Connections
        </button>

        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl border border-border bg-muted">
            <ServerIcon className="size-6 text-foreground" />
          </span>
          <h1 className="text-lg font-semibold text-foreground">Connect your own MCP server</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Give Morrow the tools from any remote MCP server — your company's internal tools, or an
            app Morrow doesn't list. The agent uses them like your other connected apps.
          </p>
        </div>

        {isSignedIn && suggested.length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted-foreground">Suggested</span>
            {suggested.map((k) => (
              <div
                key={k.url}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-xs font-medium text-foreground">{k.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{k.tagline}</div>
                </div>
                <Button
                  size="xs"
                  disabled={quickBusy !== null}
                  onClick={() => void quickConnect(k)}
                >
                  {quickBusy === k.url ? (
                    <>
                      <Loader2Icon className="size-3.5 animate-spin" /> Connecting…
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="size-3.5" /> Connect
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {isLoaded && !isSignedIn ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-5 text-center">
            <p className="text-sm text-muted-foreground">Sign in to connect an MCP server.</p>
            <Button onClick={() => openSignIn()}>Sign in</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
            <div className="flex flex-col gap-3">
              <LabeledInput
                label="Server URL"
                type="url"
                placeholder="https://mcp.example.com/mcp"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
              />
              <LabeledInput
                label="Name"
                placeholder={url ? hostLabel(url) : "e.g. Shop tools"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">Authentication</span>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Authentication">
                {AUTH_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={authType === opt.id}
                    onClick={() => setAuthType(opt.id)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition-colors",
                      authType === opt.id
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-muted text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {authType === "bearer" ? (
              <LabeledInput
                label="Token"
                type="password"
                placeholder="Sent as Authorization: Bearer …"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
              />
            ) : null}
            {authType === "header" ? (
              <div className="flex gap-2">
                <LabeledInput
                  label="Header name"
                  className="max-w-40"
                  value={headerName}
                  onChange={(e) => setHeaderName(e.target.value)}
                  autoComplete="off"
                />
                <LabeledInput
                  label="Value"
                  type="password"
                  value={headerValue}
                  onChange={(e) => setHeaderValue(e.target.value)}
                  autoComplete="off"
                />
              </div>
            ) : null}

            {error ? <p className="text-xs text-destructive">{error}</p> : null}

            <Button onClick={() => void connect()} disabled={!canConnect} className="w-full">
              {busy ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" />{" "}
                  {authType === "oauth" ? "Opening sign-in…" : "Checking the server…"}
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="size-4" />{" "}
                  {authType === "oauth" ? "Sign in & connect" : "Connect"}
                </>
              )}
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
              {authType === "oauth"
                ? "You'll sign in to the service in a popup; Morrow stores only the resulting tokens, encrypted, and refreshes them for you."
                : "We connect once to list its tools, then store the token encrypted. It's never shown again."}{" "}
              Only HTTPS servers on the public internet are supported.
            </p>
          </div>
        )}

        {servers.length > 0 ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-muted-foreground">Your MCP servers</span>
            {servers.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  {s.status === "error" ? (
                    <AlertTriangleIcon className="size-4 shrink-0 text-amber-500" />
                  ) : (
                    <CheckIcon className="size-4 shrink-0 text-emerald-500" />
                  )}
                  <div className="min-w-0">
                    <div className="truncate text-xs font-medium text-foreground">{s.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {s.status === "error" && s.lastError
                        ? s.lastError
                        : `${s.toolCount} tool${s.toolCount === 1 ? "" : "s"} · ${s.url}`}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    size="xs"
                    variant="ghost"
                    aria-label={`Check ${s.name} again`}
                    disabled={rowBusy === s.id}
                    onClick={() => void recheck(s.id)}
                  >
                    {rowBusy === s.id ? (
                      <Loader2Icon className="size-3.5 animate-spin" />
                    ) : (
                      <RefreshCwIcon className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    aria-label={`Remove ${s.name}`}
                    disabled={rowBusy === s.id}
                    onClick={() => void remove(s.id)}
                  >
                    <Trash2Icon className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SettingsPageContainer>
  );
}

/** A readable default name from a URL's host ("mcp.acme.com" → "acme"). */
function hostLabel(raw: string): string {
  try {
    const parts = new URL(raw.trim()).hostname.split(".").filter((p) => p !== "www" && p !== "mcp");
    return parts.length >= 2
      ? (parts[parts.length - 2] ?? "MCP server")
      : (parts[0] ?? "MCP server");
  } catch {
    return "MCP server";
  }
}
